import type { PusherPrivateChannel } from '@shared/api'

import { leaveChannel, listenChannel, stopListeningEvent, useChannel } from '@shared/api'
import { useToast } from '@shared/ui/toast'

import { useLessonsStore } from './store'
import { useDisciplinePrivileges } from '../discipline-privileges.hook'
import { useLessonsRefresh } from './lessons-refresh.hook'

const RECURRING_DONE_EVENT = '.discipline.bookings.recurring.done'
const RECURRING_FAILED_EVENT = '.discipline.bookings.recurring.failed'

/**
 * The recurring job reports on the discipline channel. Every viewer rereads the list; only the browser that queued
 * the job hears how it went, since the events are empty and other admins receive them too. Joining needs
 * view-disciplines: without it the list simply is not refreshed live.
 */
export const useLessonsRecurringEvents = (): void => {
  const route = useRoute()
  const toast = useToast()
  const store = useLessonsStore()
  const { canViewDisciplines } = useDisciplinePrivileges()
  const { refreshLessons } = useLessonsRefresh()

  const disciplineId = Number(route.params.id)
  const channelName = `admin.discipline.${disciplineId}`

  let channel: PusherPrivateChannel | null = null

  const isOwnJob = (): boolean => store.getRecurringPendingId === disciplineId

  const onDone = (): void => {
    if (isOwnJob()) {
      store.setRecurringPendingId(null)
      toast.success({
        title: 'Successfully created!',
        description: 'Selected recurring lesson was successfully created!',
      })
    }

    void refreshLessons()
  }

  const onFailed = (): void => {
    if (isOwnJob()) {
      store.setRecurringPendingId(null)
      toast.error('Recurring lessons could not be created, please try again')
    }

    void refreshLessons()
  }

  onMounted(() => {
    if (!canViewDisciplines.value) return

    channel = useChannel(channelName)
    listenChannel(channel, RECURRING_DONE_EVENT, onDone)
    listenChannel(channel, RECURRING_FAILED_EVENT, onFailed)
  })

  onBeforeUnmount(() => {
    if (!channel) return

    stopListeningEvent(channel, RECURRING_DONE_EVENT, onDone)
    stopListeningEvent(channel, RECURRING_FAILED_EVENT, onFailed)
    leaveChannel(channelName)
    channel = null
  })
}
